import React, { useEffect, useRef, useState } from 'react';
import { useUser } from '../../contexts/UserContext';
import { supabase } from '../../lib/supabaseClient';
import type { Session } from '@supabase/supabase-js';
import LoginModal from '../shared/ui/LoginModal';
import WaitingApproval from './WaitingApproval';
import { logger } from '../../utils/logger';

interface Props {
  children: React.ReactNode;
}

/**
 * AuthGuard - Route protection and authentication flow handler
 * 
 * Responsibilities:
 * - Protects routes based on authentication status
 * - Handles magic link callback processing
 * - Manages cross-tab communication for seamless login
 * - Shows appropriate UI (LoginModal, WaitingApproval, or children) based on auth state
 */
export default function AuthGuard({ children }: Props) {
  const { user, role, authReady } = useUser();
  const [directSession, setDirectSession] = useState<Session | null>(null);
  const [isHandling, setIsHandling] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const handledRef = useRef(false);

  const isCallback = window.location.pathname === '/auth/callback' || 
                     window.location.hash.includes('access_token=') ||
                     window.location.hash.includes('token=');

  // Debug logging in development
  if (import.meta.env.DEV) {
    logger.debug('Render', {
      hasUser: !!user,
      role,
      authReady,
      hasDirectSession: !!directSession,
      isCallback,
      pathname: window.location.pathname,
      hasHashTokens: window.location.hash.includes('access_token=')
    }, 'AuthGuard');
  }

  /**
   * Robust auth callback handler (replaces fragile race-prone logic)
   * - Clears stale localStorage tokens first
   * - Uses setSession when access_token/refresh_token present (deterministic)
   * - Falls back to verifyOtp if token_hash is present
   * - Waits up to 5s for session to appear, then forces navigation
   */
  useEffect(() => {
    if (!isCallback) return;

    if (handledRef.current) return;
    handledRef.current = true;

    (async () => {
      try {
        const qs = new URLSearchParams(window.location.search);
        const hs = new URLSearchParams(window.location.hash.replace(/^#/, ''));

        // Prefer hash tokens
        const accessToken = hs.get('access_token');
        const refreshToken = hs.get('refresh_token');
        const tokenHash = qs.get('token_hash') || qs.get('token') || hs.get('token_hash') || hs.get('token');

        // Clear stale local session keys to avoid pollution
        try {
          Object.keys(localStorage).forEach(k => {
            if (/supabase|sb-session|sb-access-token|sb-refresh-token|auth/.test(k)) {
              localStorage.removeItem(k);
            }
          });
        } catch (e) {
          if (import.meta.env.DEV) logger.warn('Failed to clear stale tokens', { error: e }, 'AuthGuard');
        }

        if (accessToken && refreshToken) {
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          if (error) throw error;
        } else if (tokenHash) {
          const type = (qs.get('type') || hs.get('type') || 'magiclink') as 'magiclink'|'invite'|'recovery';
          const { error } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type });
          if (error) throw error;
        } else {
          throw new Error('No token found in URL');
        }

        // Wait for SDK to have session or fallback poll
        const waitForSession = async (timeoutMs = 5000) => {
          const stop = Date.now() + timeoutMs;
          while (Date.now() < stop) {
            const { data } = await supabase.auth.getSession();
            if (data?.session?.user?.id) return data.session;
            await new Promise((r) => setTimeout(r, 100));
          }
          throw new Error('Session not available after setSession');
        };

        const session = await waitForSession(5000);
        
        if (import.meta.env.DEV) {
          logger.info('Session established', { email: session.user.email, role: session.user.user_metadata?.role }, 'AuthGuard');
        }
        
        setDirectSession(session);
        
        // Broadcast login success to close the original tab that requested the magic link
        // The NEW tab (this one) stays open, the OLD tab (with login modal) closes
        try {
          const channel = new BroadcastChannel('auth-events');
          channel.postMessage({
            type: 'MAGIC_LINK_SUCCESS',
            email: session.user.email,
            role: session.user.user_metadata?.role,
            timestamp: Date.now(),
            closeOriginalTab: true // Signal to close the original tab with login modal
          });
          
          // If this is a popup/opener window, notify parent to close itself
          if (window.opener && !window.opener.closed) {
            try {
              window.opener.postMessage({
                type: 'MAGIC_LINK_SUCCESS',
                email: session.user.email,
                role: session.user.user_metadata?.role,
                closeOriginalTab: true // Signal parent to close
              }, window.location.origin);
              // Close the opener (original tab with login modal)
              window.opener.close();
            } catch (e) {
              if (import.meta.env.DEV) logger.warn('Could not close opener', { error: e }, 'AuthGuard');
            }
          }
        } catch (error) {
          if (import.meta.env.DEV) logger.warn('Failed to notify tabs', { error }, 'AuthGuard');
        }
        
        // Clean URL first, then immediately navigate to dashboard
        // This ensures the user sees the dashboard immediately without clicking anything
        try {
          window.history.replaceState({}, document.title, '/');
        } catch (e) {
          if (import.meta.env.DEV) logger.warn('Failed to clean URL', { error: e }, 'AuthGuard');
        }
        
        // Force immediate navigation to dashboard - user should see Updates section immediately
        // Use setTimeout to ensure session is fully established before navigation
        setTimeout(() => {
          window.location.replace('/');
          // Fallback: if replace doesn't work, use href
          if (window.location.pathname !== '/') {
            window.location.href = '/';
          }
        }, 100);

      } catch (e: any) {
        logger.error('Auth callback error', { error: e }, 'AuthGuard');
        setErr(String(e?.message || e));
        setTimeout(() => window.location.replace('/login?error=auth_failed'), 1200);
      } finally {
        setIsHandling(false);
      }
    })();
  }, [isCallback]);

  /**
   * Clean up directSession once UserContext has synced
   * Prevents duplicate session state
   */
  useEffect(() => {
    if (user && directSession) {
      if (import.meta.env.DEV) logger.debug('UserContext caught up, clearing direct session', undefined, 'AuthGuard');
      setDirectSession(null);
      handledRef.current = false;
    }
  }, [user, directSession]);

  /**
   * Use directSession as fallback if UserContext hasn't synced yet
   * Prevents flash of login modal during callback processing
   */
  const effectiveUser = user || (directSession ? { email: directSession.user.email } : null);
  const effectiveRole = role || (directSession ? getRoleFromSession(directSession) : null);

  // Show loading during callback handling
  if (isCallback && isHandling) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-lg font-semibold theme-text mb-2">
            {err ? 'Authentication failed' : 'Signing you in...'}
          </p>
          <p className="text-sm theme-text-muted">
            {err ? String(err) : 'Please wait while we complete your sign-in'}
          </p>
        </div>
      </div>
    );
  }

  // Show loading state during initial auth check to prevent modal flash
  // This prevents the login modal from briefly appearing on page refresh
  // Only show login modal after auth check completes (authReady === true)
  if (!authReady && !directSession && !isCallback) {
    return (
      <div className="flex items-center justify-center min-h-screen theme-muted">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-sm theme-text-muted">Loading...</p>
        </div>
      </div>
    );
  }

  // TEMPORARY: Bypass auth for smoke testing
  // Set VITE_SMOKE_TEST=true in .env to enable test mode
  const SMOKE_TEST_MODE = import.meta.env.VITE_SMOKE_TEST === 'true';
  
  if (SMOKE_TEST_MODE) {
    // In smoke test mode, bypass auth and allow access
    if (import.meta.env.DEV) {
      logger.debug('SMOKE TEST MODE: Bypassing authentication', undefined, 'AuthGuard');
    }
    return <>{children}</>;
  }

  // Only show login modal if auth check is complete AND user is not authenticated
  // This ensures no flash of modal during auth check
  if (!effectiveUser && authReady) {
    if (isCallback) {
      return null; // Wait for callback handler
    }
    return <LoginModal isOpen onClose={() => {}} />;
  }

  if (effectiveRole === null) {
    return <WaitingApproval />;
  }

  return <>{children}</>;
}

/**
 * Helper: Extract role from session JWT metadata
 * Priority: user_metadata.role > app_metadata.role
 */
function getRoleFromSession(session: any): 'admin' | 'viewer' | null {
  const metaRole = session?.user?.user_metadata?.role;
  const appRole = (session?.user?.app_metadata as any)?.role;
  const role = metaRole ?? appRole;
  if (!role) return null;
  return role.toLowerCase() === 'admin' ? 'admin' : 'viewer';
}

